import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import styled, { keyframes } from 'styled-components';
import { GET_NOTICE_BOARD } from 'containers/Notice/graphQL';
import { NoticeBoard, NoticeNode } from 'containers/Notice/graphQL/types';
import { NOTICE_DETAIL_PATH } from 'constants/path';

const BANNER_HEIGHT = 50;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const AUTO_SLIDE_INTERVAL = 4000;

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: ${BANNER_HEIGHT}px;
  padding: 0 16px;
  background: rgba(122, 41, 250, 1);
  overflow: hidden;
`;

const Badge = styled.span`
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 4px;
  background: rgba(27, 28, 30, 0.8);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CarouselArea = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  overflow: hidden;
`;

const CarouselItem = styled.div<{ $isActive: boolean }>`
  display: ${({ $isActive }) => ($isActive ? 'flex' : 'none')};
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  animation: ${fadeIn} 0.4s ease;
`;

const TitleText = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PageNumber = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const NoticeBanner: React.FC = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data } = useQuery<NoticeBoard>(GET_NOTICE_BOARD, {
    variables: {
      filter: { forHostOnly: true },
      page: 1,
      size: 10,
    },
  });

  const recentNotices = useMemo(() => {
    if (!data?.board?.notices?.edges) return [];

    const now = Date.now();

    return data.board.notices.edges
      .map(edge => edge.node)
      .filter(notice => now - notice.createdAt < SEVEN_DAYS_MS);
  }, [data]);

  useEffect(() => {
    if (recentNotices.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % recentNotices.length);
    }, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [recentNotices.length]);

  if (recentNotices.length === 0) {
    return null;
  }

  const handleClickNotice = (notice: NoticeNode) => {
    navigate(`${NOTICE_DETAIL_PATH}/${notice.id}`);
  };

  return (
    <BannerContainer>
      <Badge>공지</Badge>
      <CarouselArea>
        {recentNotices.map((notice, index) => (
          <CarouselItem
            key={notice.id}
            $isActive={index === currentIndex}
            onClick={() => handleClickNotice(notice)}
          >
            <TitleText>{notice.title}</TitleText>
          </CarouselItem>
        ))}
      </CarouselArea>
      {recentNotices.length > 1 && (
        <PageNumber>
          {currentIndex + 1}/{recentNotices.length}
        </PageNumber>
      )}
    </BannerContainer>
  );
};

export default NoticeBanner;
